import React, { useCallback, useEffect, useState } from "react";
import {
    Box, Button, FormControl, InputLabel, MenuItem, Paper, Select, Tab, Tabs,
    Typography, Alert, Table, TableHead, TableRow, TableCell, TableBody,
    TableContainer, Chip, IconButton, Tooltip, CircularProgress,
} from "@mui/material";
import { SearchRounded } from "@mui/icons-material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import api from "../../../services/api";
import { useNotify } from "../../../components/Datatable/NotifyProvider";
import { useFormError } from "../../../components/Datatable/FormErrorProvider";
import { useLoading } from "../../../components/LoadingProvider";
import CustomTextField from "../../../components/CustomTextField";
import ProvisioningProgressModal from "../../../components/ProvisioningProgressModal";
import Autocomplete from "@mui/material/Autocomplete";


/**
 * EmpresaSelect local: lista apenas empresas com serviço de provisionamento ativo.
 * Usa endpoint admin dedicado em vez do /admin/empresa/all que traz tudo.
 */
function AdminEmpresaSelect({ value, handler }) {
    const [empresas, setEmpresas] = useState([]);
    const [loading, setLoading] = useState(true);
    const error = useFormError();

    useEffect(() => {
        setLoading(true);
        api.get("/admin/provisioning/olt/empresas_with_service")
            .then(res => setEmpresas(Array.isArray(res.data) ? res.data : []))
            .catch(() => setEmpresas([]))
            .finally(() => setLoading(false));
    }, []);

    return (
        <Autocomplete
            size="small"
            options={empresas}
            loading={loading}
            loadingText="Buscando..."
            noOptionsText="Nenhuma empresa com serviço de provisionamento ativo"
            value={value ? (empresas.find(e => e.id == value) || null) : null}
            onChange={(_, v) => handler(v?.id || null)}
            isOptionEqualToValue={(o, v) => v?.id && o.id == v.id}
            getOptionLabel={(o) => `${o.cnpj || ''} - ${o.name}`.trim()}
            renderInput={(params) => (
                <CustomTextField {...params} size="small" label="Empresa"
                    error={error.verify('empresa')} helperText={error.msgError('empresa')} />
            )}
        />
    );
}


/**
 * OltSelect local: dispara a busca SÓ quando `empresa` está pronta.
 * Backend filtra OLTs por prov_service active (alinhado com cliente).
 */
function AdminOltSelect({ empresa, value, handler }) {
    const [olts, setOlts] = useState([]);
    const [loading, setLoading] = useState(false);
    const error = useFormError();

    useEffect(() => {
        if (!empresa) { setOlts([]); return; }
        setLoading(true);
        api.get(`/admin/provisioning/olt/${empresa}/all`)
            .then(res => setOlts(Array.isArray(res.data) ? res.data : []))
            .catch(() => setOlts([]))
            .finally(() => setLoading(false));
    }, [empresa]);

    return (
        <Autocomplete
            size="small"
            options={olts}
            loading={loading}
            loadingText="Buscando..."
            noOptionsText={empresa ? "Nenhuma OLT pra esta empresa" : "Selecione empresa primeiro"}
            value={value ? (olts.find(o => o.id == value) || null) : null}
            onChange={(_, v) => handler(v?.id || null)}
            isOptionEqualToValue={(o, v) => v?.id && o.id == v.id}
            getOptionLabel={(o) => `${o.name} - ${o.ip}`}
            renderInput={(params) => (
                <CustomTextField {...params} size="small" label="OLT"
                    error={error.verify('olt')} helperText={error.msgError('olt')} />
            )}
        />
    );
}



// Gera task_id curto (8 hex chars) usando crypto.randomUUID se disponível
function genTaskId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID().replace(/-/g, "").slice(0, 8);
    return Math.random().toString(16).slice(2, 10);
}

const FORM_WIDTH = 500;

// Tema light local — força fundo branco nos selects/inputs desta tela,
// sem afetar outras telas do admin.
const lightTheme = createTheme({
    palette: {
        mode: "light",
        primary: { main: "#e01b29" },
        background: { default: "#f5f5f5", paper: "#ffffff" },
    },
});


export default function OnuProvisioning() {
    useEffect(() => { document.title = "IPV7 Olimpo - Provisionamento (Admin)"; }, []);

    const [empresa, setEmpresa] = useState(null);
    const [tab, setTab] = useState(0);
    const [taskId, setTaskId] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const onStarted = (tid) => {
        if (tid) {
            setTaskId(tid);
            setModalOpen(true);
        }
    };

    return (
        <ThemeProvider theme={lightTheme}>
            <Box sx={{
                minHeight: "calc(100vh - 64px)",
                display: "flex", justifyContent: "center", alignItems: "flex-start",
                bgcolor: "background.default",
                py: 4, px: 2,
            }}>
                <Box sx={{ width: "100%", maxWidth: FORM_WIDTH }}>
                    <Box sx={{ textAlign: "center", mb: 3 }}>
                        <Typography variant="h5" color="text.primary">
                            Provisionamento{" "}
                            <Typography component="span" variant="caption" color="text.secondary">
                                (em nome de empresa)
                            </Typography>
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <AdminEmpresaSelect value={empresa} handler={setEmpresa} />
                    </Box>

                    {!empresa && (
                        <Alert severity="info">
                            Selecione uma empresa pra desbloquear as operações.
                        </Alert>
                    )}

                    {empresa && (
                        <Paper variant="outlined">
                            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
                                <Tab label="Provisionar" />
                                <Tab label="Desprovisionar" />
                                <Tab label="Clientes" />
                                <Tab label="Sinal" />
                            </Tabs>
                            <Box sx={{ p: 2 }}>
                                {tab === 0 && <ProvForm empresa={empresa} onStarted={onStarted} />}
                                {tab === 1 && <RemForm empresa={empresa} onStarted={onStarted} />}
                                {tab === 2 && <ListClientsForm empresa={empresa} onStarted={onStarted} />}
                                {tab === 3 && <SignalForm empresa={empresa} onStarted={onStarted} />}
                            </Box>
                        </Paper>
                    )}
                </Box>

                <ProvisioningProgressModal
                    open={modalOpen}
                    taskId={taskId}
                    mode="admin"
                    streamPath="/admin/provisioning/stream"
                    onClose={() => setModalOpen(false)}
                />
            </Box>
        </ThemeProvider>
    );
}


// ─── Provisionar (espelha ProvDetail do cliente) ─────────────────────────────

function ProvForm({ empresa, onStarted }) {
    const notify = useNotify();
    const error = useFormError();
    const { loading, setLoading } = useLoading();
    const [state, setState] = useState({
        sn: "", vlan: "", line: "", srv: "", description: "",
        roteada: 1, olt: null, board: "", port: "", gemport: "",
    });
    const [freeOnus, setFreeOnus] = useState([]);
    const [lines, setLines] = useState([]);
    const [srvs, setSrvs] = useState([]);
    const [searchingFree, setSearchingFree] = useState(false);

    const handleState = useCallback((event) => {
        setState({ ...state, [event.target.name]: event.target.value });
    }, [state]);

    // Buscar SNs disponíveis + lines + srvs na OLT (botão lupa)
    const searchFreeOnus = async () => {
        if (!state.olt || state.board === "" || state.port === "") return;
        setSearchingFree(true);
        setFreeOnus([]); setLines([]); setSrvs([]);
        const tid = genTaskId();
        onStarted(tid);
        try {
            const res = await api.post(
                `/admin/provisioning/onu_operational/onu/free_detail/${empresa}?task_id=${tid}`,
                { olt: state.olt, board: state.board, port: state.port },
            );
            if (res.data?.error) {
                notify.setNotify({ open: true, message: res.data.error, severity: "error" });
                return;
            }
            const r = res.data?.result || {};
            setFreeOnus(r.sns || []);
            setLines(r.lines || []);
            setSrvs(r.srvs || []);
        } catch (e) {
            notify.setNotify({ open: true, message: "Falha: " + e.message, severity: "error" });
        } finally { setSearchingFree(false); }
    };

    const submit = () => {
        error.setErrors([]);
        setLoading(true);
        const payload = { ...state, roteada: state.roteada === 2 };
        const tid = genTaskId();
        onStarted(tid);  // abre modal imediato — SSE pega eventos ao vivo
        api.post(`/admin/provisioning/onu_operational/prov/detail/${empresa}?task_id=${tid}`, payload)
            .then(res => {
                if (res.data?.error) {
                    notify.setNotify({ open: true, message: res.data.error, severity: "error" });
                    return;
                }
                if (res.data?.errors) {
                    error.setErrors(res.data.errors);
                    return;
                }
                onStarted(res.data?.task_id);
                setState({ sn: "", vlan: "", line: "", srv: "", description: "",
                           roteada: 1, olt: null, board: "", port: "", gemport: "" });
                notify.setNotify({ open: true, message: "ONU provisionada", severity: "success" });
            })
            .catch(e => notify.setNotify({ open: true, message: "Falha: " + e.message, severity: "error" }))
            .finally(() => setLoading(false));
    };

    const canSearch = state.olt && state.board !== "" && state.port !== "";

    return (
        <Box display="flex" flexDirection="column" gap={1.5}>
            <AdminOltSelect empresa={empresa}
                value={state.olt} handler={(e) => setState({ ...state, olt: e })} />
            <Box display="flex" gap={1} alignItems="flex-start"
                 sx={{ flexDirection: { xs: "column", sm: "row" } }}>
                <CustomTextField size="small" sx={{ flexGrow: 1 }} type="number"
                    error={error.verify('board')} helperText={error.msgError('board')}
                    label="Placa" name="board" value={state.board} onChange={handleState} />
                <CustomTextField size="small" sx={{ flexGrow: 1 }} type="number"
                    error={error.verify('port')} helperText={error.msgError('port')}
                    label="Porta" name="port" value={state.port} onChange={handleState} />
                <Tooltip title="Buscar SNs disponíveis na OLT">
                    <span>
                        <Button variant="contained" onClick={searchFreeOnus}
                                disabled={!canSearch || searchingFree}
                                sx={{ height: 40, minWidth: 48 }}>
                            {searchingFree ? <CircularProgress size={18} color="inherit" />
                                           : <SearchRounded />}
                        </Button>
                    </span>
                </Tooltip>
            </Box>

            <FormControl size="small" disabled={freeOnus.length === 0}>
                <InputLabel id="sn-label">SN</InputLabel>
                <Select labelId="sn-label" label="SN" name="sn"
                        value={state.sn} onChange={handleState}>
                    {freeOnus.length === 0 && (
                        <MenuItem value=""><em>Clique na lupa pra buscar</em></MenuItem>
                    )}
                    {freeOnus.map((sn, i) => (
                        <MenuItem key={i} value={typeof sn === 'string' ? sn : sn.sn || sn}>
                            {typeof sn === 'string' ? sn : (sn.sn || JSON.stringify(sn))}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Box display="flex" gap={1} sx={{ flexDirection: { xs: "column", sm: "row" } }}>
                <FormControl size="small" sx={{ flexGrow: 1 }} disabled={srvs.length === 0}>
                    <InputLabel id="srv-label">Service profile</InputLabel>
                    <Select labelId="srv-label" label="Service profile" name="srv"
                            value={state.srv} onChange={handleState}>
                        {srvs.length === 0 && <MenuItem value=""><em>Buscar primeiro</em></MenuItem>}
                        {srvs.map((s, i) => (
                            <MenuItem key={i} value={s.id ?? s}>
                                {s.id ? `${s.id} - ${s.name || s.description || ''}` : String(s)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ flexGrow: 1 }} disabled={lines.length === 0}>
                    <InputLabel id="line-label">Line profile</InputLabel>
                    <Select labelId="line-label" label="Line profile" name="line"
                            value={state.line} onChange={handleState}>
                        {lines.length === 0 && <MenuItem value=""><em>Buscar primeiro</em></MenuItem>}
                        {lines.map((l, i) => (
                            <MenuItem key={i} value={l.id ?? l}>
                                {l.id ? `${l.id} - ${l.name || l.description || ''}` : String(l)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            <CustomTextField size="small" type="number"
                error={error.verify('vlan')} helperText={error.msgError('vlan')}
                label="Vlan" name="vlan" value={state.vlan} onChange={handleState} />
            <CustomTextField size="small" type="number"
                error={error.verify('gemport')} helperText={error.msgError('gemport')}
                label="Gemport" name="gemport" value={state.gemport} onChange={handleState} />
            <FormControl size="small">
                <InputLabel id="mode-prov-select-label">Modo</InputLabel>
                <Select
                    labelId="mode-prov-select-label"
                    value={state.roteada}
                    label="Modo"
                    onChange={e => setState({ ...state, roteada: e.target.value })}
                >
                    <MenuItem value={1}>Bridge</MenuItem>
                    <MenuItem value={2}>Roteada</MenuItem>
                </Select>
            </FormControl>
            <CustomTextField size="small"
                error={error.verify('description')} helperText={error.msgError('description')}
                label="Descrição" name="description" value={state.description} onChange={handleState} />
            <Button variant="contained" onClick={submit} disabled={loading || !state.olt}>
                Provisionar
            </Button>
        </Box>
    );
}


// ─── Desprovisionar ──────────────────────────────────────────────────────────

function RemForm({ empresa, onStarted }) {
    const notify = useNotify();
    const error = useFormError();
    const { loading, setLoading } = useLoading();
    const [state, setState] = useState({ olt: null, board: "", port: "", sn: "" });
    const handleState = useCallback((e) => setState({ ...state, [e.target.name]: e.target.value }), [state]);

    const submit = () => {
        if (!window.confirm(`Desprovisionar ONU ${state.sn}?`)) return;
        error.setErrors([]);
        setLoading(true);
        const tid = genTaskId();
        onStarted(tid);
        api.post(`/admin/provisioning/onu_operational/rem/detail/${empresa}?task_id=${tid}`, state)
            .then(res => {
                if (res.data?.error) {
                    notify.setNotify({ open: true, message: res.data.error, severity: "error" });
                    return;
                }
                if (res.data?.errors) {
                    error.setErrors(res.data.errors);
                    return;
                }
                onStarted(res.data?.task_id);
                setState({ olt: null, board: "", port: "", sn: "" });
                notify.setNotify({ open: true, message: "ONU desprovisionada", severity: "success" });
            })
            .catch(e => notify.setNotify({ open: true, message: "Falha: " + e.message, severity: "error" }))
            .finally(() => setLoading(false));
    };

    return (
        <Box display="flex" flexDirection="column" gap={1.5}>
            <AdminOltSelect empresa={empresa}
                value={state.olt} handler={(e) => setState({ ...state, olt: e })} />
            <Box display="flex" gap={1} sx={{ flexDirection: { xs: "column", sm: "row" } }}>
                <CustomTextField size="small" sx={{ flexGrow: 1 }} type="number"
                    error={error.verify('board')} helperText={error.msgError('board')}
                    label="Placa" name="board" value={state.board} onChange={handleState} />
                <CustomTextField size="small" sx={{ flexGrow: 1 }} type="number"
                    error={error.verify('port')} helperText={error.msgError('port')}
                    label="Porta" name="port" value={state.port} onChange={handleState} />
            </Box>
            <CustomTextField size="small"
                error={error.verify('sn')} helperText={error.msgError('sn')}
                label="SN" name="sn" value={state.sn} onChange={handleState} />
            <Button variant="contained" color="error" onClick={submit} disabled={loading || !state.olt}>
                Desprovisionar
            </Button>
        </Box>
    );
}


// ─── Consultar clientes da OLT (lista ONUs em board/port) ────────────────────

function ListClientsForm({ empresa, onStarted }) {
    const notify = useNotify();
    const error = useFormError();
    const { loading, setLoading } = useLoading();
    const [state, setState] = useState({ olt: null, board: "", port: "" });
    const [clients, setClients] = useState(null);
    const handleState = useCallback((e) => setState({ ...state, [e.target.name]: e.target.value }), [state]);

    const submit = () => {
        error.setErrors([]);
        setClients(null);
        setLoading(true);
        const tid = genTaskId();
        onStarted(tid);
        api.post(`/admin/provisioning/onu_operational/onu/all/${empresa}?task_id=${tid}`, state)
            .then(res => {
                if (res.data?.error) {
                    notify.setNotify({ open: true, message: res.data.error, severity: "error" });
                    return;
                }
                onStarted(res.data?.task_id);
                const result = res.data?.result || res.data;
                if (Array.isArray(result)) setClients(result);
            })
            .catch(e => notify.setNotify({ open: true, message: "Falha: " + e.message, severity: "error" }))
            .finally(() => setLoading(false));
    };

    return (
        <Box display="flex" flexDirection="column" gap={1.5}>
            <AdminOltSelect empresa={empresa}
                value={state.olt} handler={(e) => setState({ ...state, olt: e })} />
            <Box display="flex" gap={1} sx={{ flexDirection: { xs: "column", sm: "row" } }}>
                <CustomTextField size="small" sx={{ flexGrow: 1 }} type="number"
                    error={error.verify('board')} helperText={error.msgError('board')}
                    label="Placa" name="board" value={state.board} onChange={handleState} />
                <CustomTextField size="small" sx={{ flexGrow: 1 }} type="number"
                    error={error.verify('port')} helperText={error.msgError('port')}
                    label="Porta" name="port" value={state.port} onChange={handleState} />
            </Box>
            <Button variant="contained" onClick={submit}
                disabled={loading || !state.olt || state.board === "" || state.port === ""}>
                Listar clientes
            </Button>

            {clients && (
                <Box mt={1}>
                    <Typography variant="caption" color="text.secondary">
                        {clients.length} cliente(s) encontrado(s)
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.5 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>SN</TableCell>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Descrição</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {clients.map((c, i) => (
                                    <TableRow key={i} hover>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>{c.sn || c.SN || '-'}</TableCell>
                                        <TableCell>{c.id ?? c.ont_id ?? '-'}</TableCell>
                                        <TableCell>
                                            <Chip size="small"
                                                label={c.status || c.state || '-'}
                                                color={String(c.status).toLowerCase() === 'online' ? 'success' : 'default'} />
                                        </TableCell>
                                        <TableCell>{c.description || c.desc || '-'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
        </Box>
    );
}


// ─── Consultar sinal ─────────────────────────────────────────────────────────

function SignalForm({ empresa, onStarted }) {
    const notify = useNotify();
    const error = useFormError();
    const { loading, setLoading } = useLoading();
    const [state, setState] = useState({ olt: null, sn: "" });
    const [signal, setSignal] = useState(null);
    const handleState = useCallback((e) => setState({ ...state, [e.target.name]: e.target.value }), [state]);

    const submit = () => {
        error.setErrors([]);
        setSignal(null);
        setLoading(true);
        const tid = genTaskId();
        onStarted(tid);
        api.post(`/admin/provisioning/onu_operational/onu/signal/${empresa}?task_id=${tid}`, state)
            .then(res => {
                if (res.data?.error) {
                    notify.setNotify({ open: true, message: res.data.error, severity: "error" });
                    return;
                }
                onStarted(res.data?.task_id);
                setSignal(res.data?.result ?? null);
            })
            .catch(e => notify.setNotify({ open: true, message: "Falha: " + e.message, severity: "error" }))
            .finally(() => setLoading(false));
    };

    return (
        <Box display="flex" flexDirection="column" gap={1.5}>
            <AdminOltSelect empresa={empresa}
                value={state.olt} handler={(e) => setState({ ...state, olt: e })} />
            <CustomTextField size="small"
                error={error.verify('sn')} helperText={error.msgError('sn')}
                label="SN" name="sn" value={state.sn} onChange={handleState} />
            <Button variant="contained" onClick={submit} disabled={loading || !state.olt || !state.sn}>
                Consultar sinal
            </Button>

            {signal !== null && (
                <Paper variant="outlined" sx={{ p: 1.5, mt: 1, fontFamily: 'monospace', fontSize: 13 }}>
                    {typeof signal === 'object' ? JSON.stringify(signal, null, 2) : String(signal)}
                </Paper>
            )}
        </Box>
    );
}
